import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import AppShell from './components/layout/app-shell';
import Header from './components/layout/header';
import BottomNav from './components/layout/bottom-nav';
import FloatingAssistantButton from './components/ui/floating-assistant-button';
import Login from './pages/Login';
import Welcome from './pages/Welcome';
import Orders from './pages/Orders';
import Missions from './pages/Missions';
import Promotions from './pages/Promotions';
import Profile from './pages/Profile';
import Assistant from './pages/Assistant';

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user, token } = useAuth();
  
  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Đang tải...</p>
        </div>
      </div>
    );
  }
  
  // Check if we have valid authentication data
  const hasValidAuth = user && token && isAuthenticated;
  
  
  if (!hasValidAuth) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

// Navigation items
const navItems = [
  { id: 'orders', label: 'Đơn hàng', icon: 'shopping-cart', href: '/orders', badge: 3 },
  { id: 'missions', label: 'Nhiệm vụ', icon: 'target', href: '/missions' },
  { id: 'home', label: 'Trang chủ', icon: 'home', href: '/' },
  { id: 'promotions', label: 'Khuyến mãi', icon: 'gift', href: '/promotions' },
  { id: 'profile', label: 'Cá nhân', icon: 'user', href: '/profile' },
];

// Main App Layout Component
function AppLayout() {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleNavClick = (item: any) => {
    navigate(item.href);
  };

  const getCurrentActiveItem = () => {
    return navItems.find(item => item.href === location.pathname)?.id;
  };

  // Check if current page is Assistant
  const isAssistantPage = location.pathname === '/assistant';

  return (
    <AppShell>
        {/* Only show header and navigation if not on Assistant page */}
        {!isAssistantPage && (
          <>
            <Header
              title="Pango Outlet Assistant"
              outletname={user ? `${user.name}` : "Bạn"}
              user={{
                name: user?.name || "User",
                avatar: user?.avatar,
                badge: user?.scoring?.score_tier || "Gold"
              }}
              onThemeToggle={toggleTheme}
              theme={theme}
            />
            
            <BottomNav
              items={navItems}
              activeItem={getCurrentActiveItem()}
              onItemClick={handleNavClick}
            />
          </>
        )}
        
        <main className={`min-h-screen ${!isAssistantPage ? 'pb-16 pt-20' : ''}`}>
          <Routes>
            <Route path="/" element={<Welcome />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/missions" element={<Missions />} />
            <Route path="/promotions" element={<Promotions />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/assistant" element={<Assistant />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        
        {/* Floating Assistant Button - only show if not on Assistant page */}
        {!isAssistantPage && <FloatingAssistantButton />}
      </AppShell>
  );
}

// Main App Component
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Login Route */}
            <Route path="/login" element={<Login />} />
            
            {/* Protected Routes */}
            <Route path="/*" element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            } />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;